import { Component, inject, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { NewsService } from '../../../shared/services/news/news.service';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatTimepickerModule } from '@angular/material/timepicker';
import { CommonModule } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { finalize } from 'rxjs';
import {
  provideNativeDateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MY_DATE_FORMATS } from '../edit-detail-incorporate/edit-detail-incorporate.component';
import { UploadFilesComponent } from '../../../shared/components/upload-files/upload-files.component';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { LoaderService } from '../../../shared/services/loader/loader.service';
import { QuillModule } from 'ngx-quill';
import { QuillConfigService } from '../../../shared/services/quill-config.service';
import { StorageService } from '../../../shared/services/storage/storage.service';
import { RolesService } from '../../../shared/services/roles/roles.service';

@Component({
  selector: 'app-news-form',
  imports: [
    CommonModule,
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatInputModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDatepickerModule,
    FormsModule,
    MatTimepickerModule,
    UploadFilesComponent,
    RouterLink,
    QuillModule,
    LoaderComponent,
  ],
  providers: [
    provideNativeDateAdapter(),
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'es-ES' },
  ],
  templateUrl: './news-form.component.html',
  styleUrls: ['./news-form.component.css', '../../../shared/styles/layout.css'],
})
export class NewsFormComponent {
  // Mode management
  isEditMode: boolean = false;
  newsId: number | null = null;

  // Common properties
  modules: any;
  myroles: any = {};
  newsForm: FormGroup;
  news: any = {};
  isSubmitting = false;
  attemptedSubmit = false; // used to trigger validation messages after first submit attempt

  // Tag handling
  tagsValue: any[] = [];
  tagNamesString: string = '';

  // File handling
  selectedFiles: File[] = [];
  selectedImage: File | null = null;
  imageRemoved: boolean = false;

  @ViewChild(UploadFilesComponent) uploadFilesComponent!: UploadFilesComponent;

  private _snackBar = inject(MatSnackBar);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private newsService: NewsService,
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private loaderService: LoaderService,
    private quillConfigService: QuillConfigService,
    private storageService: StorageService,
    private rolesService: RolesService
  ) {
    this.modules = this.quillConfigService.getDefaultModules();

    this.newsForm = this.fb.group({
      name: ['', [Validators.required]],
      content: ['', [Validators.required]],
      category: ['', [Validators.required]],
      featured: [false],
      notifications: [false],
      allow_comments: [false],
      publication_date: [''],
      publication_time: ['10:00'], // Default time is 10:00
    });
  }

  get isLoading() {
    return this.loaderService.getLoader();
  }

  ngOnInit() {
    this.route.params.subscribe((params) => {
      if (params['id']) {
        this.isEditMode = true;
        this.newsId = +params['id'];
        this.makePublicationDateRequired();
        this.loaderService.showLoader();
        this.loadNewsData();
      } else {
        this.isEditMode = false;
        this.newsId = null;
      }
    });
    this.loadRoles();
  }

  private makePublicationDateRequired(): void {
    const publicationDateControl = this.newsForm.get('publication_date');
    if (publicationDateControl) {
      publicationDateControl.setValidators([Validators.required]);
      publicationDateControl.updateValueAndValidity();
    }
  }

  loadRoles() {
    const permissions = this.rolesService.getPermissionsForFunctionality(
      'news',
      this.storageService
    );

    if (permissions) {
      this.myroles = permissions;

      const permissionMessage = this.isEditMode
        ? 'No tienes permisos para editar una noticia'
        : 'No tienes permisos para añadir una noticia';

      this.rolesService.checkWritePermission(
        'news',
        this.storageService,
        this._snackBar,
        this.router,
        '/home/communication/news',
        permissionMessage
      );
    } else {
      //console.log('No user roles found in storage');
    }
  }

  private loadNewsData(): void {
    if (!this.newsId) return;

    this.newsService.getNewsId(this.newsId).subscribe({
      next: (data) => {
        // console.log('Loaded news data:', data);
        this.news = data;

        // Extract time from publication_date
        let publicationTime = '10:00'; // Default time
        if (this.news.publication_date) {
          try {
            const dateObj = new Date(this.news.publication_date);
            if (!isNaN(dateObj.getTime())) {
              const hours = dateObj.getUTCHours().toString().padStart(2, '0');
              const minutes = dateObj
                .getUTCMinutes()
                .toString()
                .padStart(2, '0');
              publicationTime = `${hours}:${minutes}`;
            }
          } catch (e) {
            console.error('Error extracting time from publication_date:', e);
          }
        }

        // Set the form values from the loaded news data
        this.newsForm.patchValue({
          name: this.news.title,
          content: this.news.content || this.news.description, // Handle both field names - prioritize 'content'
          category: this.news.category,
          featured: this.news.featured,
          notifications: this.news.notifications || false,
          allow_comments: this.news.allow_comments,
          publication_date: this.news.publication_date,
          publication_time: publicationTime,
        });

        // Process tags
        this.tagsValue = this.news.tags || [];
        this.tagNamesString = this.tagsValue
          .map((tag: any) => tag.name)
          .join(', ');
        this.loaderService.hideLoader();
      },
      error: (error) => {
        this.loaderService.hideLoader();
        console.error('Error fetching news data', error);
        this.snackBar.open('Error al cargar los datos', 'Cerrar', {
          duration: 3000,
        });
      },
    });
  }

  // Helper method to combine date and time
  getCombinedDateTime(): string | null {
    const date = this.newsForm.get('publication_date')?.value;
    const time = this.newsForm.get('publication_time')?.value || '10:00';
    if (!date) return null;
    const [hh, mm] = time.split(':').map(Number);
    const base = date instanceof Date ? date : new Date(date);
    if (isNaN(base.getTime())) return null;
    const local = new Date(
      base.getFullYear(),
      base.getMonth(),
      base.getDate(),
      hh,
      mm,
      0,
      0
    );
    return local.toISOString();
  }

  // Update tags method
  updateTags(tagString: string): void {
    this.tagNamesString = tagString;

    // Split the input by commas and create tag-like objects
    const tagNames = tagString
      .split(',')
      .map((name) => name.trim())
      .filter((name) => name);

    // Keep the original IDs if available, or set to null for new tags
    this.tagsValue = tagNames.map((name) => {
      const existingTag = this.tagsValue.find((t) => t.name === name);
      return existingTag || { name, id: null };
    });
  }

  onFileSelected(files: File[]): void {
    if (files && files.length > 0) {
      this.selectedImage = files[0];
      this.selectedFiles = files;
      //console.log('Files selected:', files);
    }
  }

  removeImage(): void {
    // console.log('Removing image, previous URL:', this.news.image_url);
    this.news.image_url = null;
    this.imageRemoved = true;
  }

  // --- Image requirement helpers ---
  private imageIsRequired(): boolean {
    return !!this.newsForm.get('featured')?.value; // image required only when featured
  }

  private hasImageSelectedOrExisting(): boolean {
    if (this.selectedImage) return true;
    if (this.news?.image_url && !this.imageRemoved) return true;
    return false;
  }

  get showImageRequiredError(): boolean {
    return (
      !!this.imageIsRequired() &&
      !this.hasImageSelectedOrExisting() &&
      !!(this.attemptedSubmit || this.newsForm.get('featured')?.touched)
    );
  }

  saveChanges(): void {
    this.loaderService.showLoader();
    if (this.isSubmitting) return;
    this.attemptedSubmit = true;

    // Check form validity
    const isFormValid = this.isEditMode
      ? this.newsForm.valid
      : this.newsForm.valid;

    // Extra validation: image required when featured
    if (this.imageIsRequired() && !this.hasImageSelectedOrExisting()) {
      this.loaderService.hideLoader();
      this.snackBar.open(
        'La imagen es obligatoria cuando la noticia es destacada',
        'Cerrar',
        {
          duration: 5000,
        }
      );
      return;
    }

    if (!isFormValid) {
      this.loaderService.hideLoader();
      // Mark all controls as touched to trigger validation visuals
      Object.keys(this.newsForm.controls).forEach((key) => {
        const control = this.newsForm.get(key);
        control?.markAsTouched();
      });

      this.snackBar.open(
        'Por favor completa todos los campos requeridos',
        'Cerrar',
        { duration: 5000 }
      );
      return;
    }

    this.isSubmitting = true;

    if (this.isEditMode) {
      this.updateNews();
    } else {
      this.createNews();
    }
  }

  private createNews(): void {
    // Get the combined date and time
    const publicationDateTime = this.getCombinedDateTime();

    // Determine if the publication date is ahead of today
    let published = true;
    if (publicationDateTime) {
      const today = new Date();
      const publicationDateObj = new Date(publicationDateTime);
      if (publicationDateObj > today) {
        published = false;
      }
    }

    // Create FormData instead of regular object
    const formData = new FormData();

    // Get content with special handling for emojis
    let content = this.newsForm.get('content')?.value;

    // Try to get the actual HTML content from the Quill editor to preserve emojis
    try {
      const quillEditor = document.querySelector('.ql-editor') as HTMLElement;
      if (quillEditor && quillEditor.innerHTML) {
        const htmlContent = quillEditor.innerHTML;

        // Always use the HTML content from the editor as it preserves emojis better
        //console.log('Using HTML content from Quill editor to preserve emojis');
        content = htmlContent;
      }
    } catch (error) {
      //console.log('Could not extract HTML content, using form value');
    }

    if (content) {
      formData.append('content', content);

      // Debug: Log the content to verify emojis are present
      //console.log('Creating news with content:', content);
    }

    // Add other form fields
    formData.append('title', this.newsForm.get('name')?.value);
    formData.append('category', this.newsForm.get('category')?.value);
    formData.append(
      'featured',
      this.newsForm.get('featured')?.value ? 'true' : 'false'
    );
    formData.append(
      'notifications',
      this.newsForm.get('notifications')?.value ? 'true' : 'false'
    );
    formData.append('published', published ? 'true' : 'false');
    formData.append('tags_string', this.tagNamesString || '');

    if (publicationDateTime) {
      formData.append('publication_date', publicationDateTime);
    }

    // Add image if selected
    if (this.selectedFiles.length > 0) {
      formData.append('image', this.selectedFiles[0]);
    }

    // Debug: Log the content to verify emojis are present
    //console.log('Creating news with content:', content);

    // Call the service to post the news using FormData
    this.newsService
      .postNews(formData)
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: (response) => {
          this.loaderService.hideLoader();
          this.snackBar.open('Noticia creada exitosamente', 'Cerrar', {
            duration: 3000,
          });
          this.router.navigate(['/home/communication/news']);
        },
        error: (error) => {
          this.loaderService.hideLoader();
          console.error('Error creating news:', error);
          this.snackBar.open('Error al crear la noticia', 'Cerrar', {
            duration: 3000,
          });
        },
      });
  }

  private updateNews(): void {
    if (!this.newsId) return;

    const formData = new FormData();

    // Get content with special handling for emojis
    let content = this.newsForm.get('content')?.value;

    // Try to get the actual HTML content from the Quill editor to preserve emojis
    try {
      const quillEditor = document.querySelector('.ql-editor') as HTMLElement;
      if (quillEditor && quillEditor.innerHTML) {
        const htmlContent = quillEditor.innerHTML;

        // Always use the HTML content from the editor as it preserves emojis better
        //console.log('Using HTML content from Quill editor to preserve emojis');
        content = htmlContent;
      }
    } catch (error) {
      //console.log('Could not extract HTML content, using form value');
    }

    if (content) {
      // Debug: Log the raw content
      //console.log('Raw content from form:', content);

      // Improved emoji detection regex that covers more emoji ranges
      const emojiRegex =
        /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F900}-\u{1F9FF}]|[\u{1F200}-\u{1F2FF}]|[\u{1F100}-\u{1F1FF}]|[\u{FE00}-\u{FE0F}]|[\u{1F018}-\u{1F270}]/gu;
      const hasEmojis = emojiRegex.test(content);

      //console.log('Content contains emojis:', hasEmojis);
      // console.log(
      //   'Content character codes:',
      //   content.split('').map((c: string) => c.charCodeAt(0))
      // );

      formData.append('content', content);
    }

    // Debug: Check what's actually in FormData
    //console.log('FormData content being sent:', formData.get('content')); // Add other form fields to FormData
    formData.append('title', this.newsForm.get('name')?.value);
    formData.append('category', this.newsForm.get('category')?.value);
    formData.append(
      'featured',
      this.newsForm.get('featured')?.value ? 'true' : 'false'
    );
    formData.append(
      'notifications',
      this.newsForm.get('notifications')?.value ? 'true' : 'false'
    );
    // Handle publication date with time and determine published status
    const combinedDateTime = this.getCombinedDateTime();
    let published = this.news.published || false; // Use existing published state as default
    if (combinedDateTime) {
      const today = new Date();
      const publicationDateObj = new Date(combinedDateTime);
      published = publicationDateObj <= today;
      formData.append('publication_date', combinedDateTime);
    }
    formData.append('published', published ? 'true' : 'false');

    // Add tags
    if (this.tagNamesString) {
      formData.append('tags_string', this.tagNamesString);
    }

    // Debug log the image status
    //console.log('Image status before save:', {
    // selectedImage: this.selectedImage ? this.selectedImage.name : 'none',
    // imageRemoved: this.imageRemoved,
    // currentImageUrl: this.news.image_url,
    // });

    // Add image if a new one is selected
    if (this.selectedImage) {
      // console.log('Adding new image file:', this.selectedImage.name);
      formData.append('image', this.selectedImage);
    }

    // If image was removed, send a special flag
    if (this.imageRemoved) {
      // console.log('Setting remove_image flag to true');
      formData.append('remove_image', 'true');
    }

    // Verify the formData contents
    // console.log('FormData contents:');
    // formData.forEach((value, key) => {
    //  console.log(`${key}: ${value instanceof File ? value.name : value}`);
    // });

    // Update the news
    this.newsService
      .putNews(this.newsId, formData)
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: (response) => {
          //    console.log('News updated successfully:', response);
          this.loaderService.hideLoader();
          this.snackBar.open('Noticia actualizada correctamente', 'Cerrar', {
            duration: 3000,
          });
          this.router.navigate(['/home/communication/news']);
        },
        error: (error) => {
          console.error('Error updating news:', error);
          this.loaderService.hideLoader();
          this.snackBar.open('Error al actualizar la noticia', 'Cerrar', {
            duration: 3000,
          });
        },
      });
  }

  cancel(): void {
    this.router.navigate(['/home/communication/news']);
  }
}
